import { MovementBlocker } from "components/Modals/MovementBlocker";
import { Dispatch, SetStateAction, useMemo, useRef } from "react";
import { Box, Button, Grid, IconButton, TextField, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGridPro, GridColDef, GridRenderCellParams, GridSelectionModel } from "@mui/x-data-grid-pro";
import { Formik } from "formik";
import { Link } from "react-router-dom";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { defaultInventoryFilters } from "constants/default_inventory_filters";
import { WarehouseLocationLink } from "components/Locations/WarehouseLocationLink";
import useMoveItems, { display, MoveRow } from "hooks/useMoveItems";
import { PageBreadcrumbs, PageTitle } from "pages/Layouts/components/PageTitleContainer";
import { AutoPagePaperLayout } from "pages/Layouts/AutoPagePaperLayout";

const MoveGrid = ({
  rows,
  removeRow,
  selected,
  setSelected,
}: {
  rows: MoveRow[];
  selected: GridSelectionModel;
  setSelected: Dispatch<SetStateAction<GridSelectionModel>>;
  removeRow: (rowId: string) => void;
}) => {
  const columns: GridColDef[] = useMemo(
    () => [
      { field: "kindLabel", headerName: "Kind", width: 120 },
      {
        field: "recordId",
        headerName: "ID",
        width: 130,
        renderCell: (params: GridRenderCellParams<MoveRow>) => {
          if (params.row.kind === "location") {
            return <Link to={`/locations/${params.row.recordId}/edit`}>{params.row.label}</Link>;
          }
          if (params.row.pickup) {
            return (
              <Link to={`/pickups/${params.row.pickup.uuid}/inventories/${params.row.recordId}`}>
                {params.row.recordId}
              </Link>
            );
          }
          return <div>{params.row.recordId}</div>;
        },
      },
      {
        field: "pickup",
        headerName: "Pickup",
        width: 100,
        valueGetter: (params) => params.row.pickup?.id,
        renderCell: (params: GridRenderCellParams<MoveRow>) => {
          if (!params.row.pickup) return <div>{display(null)}</div>;
          return (
            <Link to={`/pickups/${params.row.pickup.uuid}${defaultInventoryFilters}`}>{params.row.pickup.id}</Link>
          );
        },
      },
      { field: "lineId", headerName: "Line ID", width: 150, valueFormatter: (params) => display(params.value) },
      { field: "sku", headerName: "SKU", width: 150, valueFormatter: (params) => display(params.value) },
      { field: "deviceType", headerName: "Device", width: 175, valueFormatter: (params) => display(params.value) },
      { field: "manufacturer", headerName: "Brand", width: 125, valueFormatter: (params) => display(params.value) },
      { field: "securityLevel", headerName: "Security", width: 150, valueFormatter: (params) => display(params.value) },
      {
        field: "enrolledStatus",
        headerName: "Enrolled Status",
        width: 125,
        valueFormatter: (params) => display(params.value),
      },
      {
        field: "warehouseLocation",
        headerName: "Location",
        width: 175,
        valueFormatter: (params) => display(params.value),
        renderCell: ({ row }: GridRenderCellParams<MoveRow>) => (
          <WarehouseLocationLink code={row.warehouseLocation} locationId={row.locationId} />
        ),
      },
      {
        field: "serialNumber",
        headerName: "Serial Number",
        width: 150,
        valueFormatter: (params) => display(params.value),
      },
      {
        field: "moveFailure",
        headerName: "Can't move",
        width: 280,
        renderCell: ({ row }: GridRenderCellParams<MoveRow>) =>
          row.moveFailure ? (
            <Typography variant="body2" color="error">
              {row.moveFailure}
            </Typography>
          ) : (
            <div>{display(null)}</div>
          ),
      },
      {
        field: "inventoryImages",
        headerName: "Images",
        width: 125,
        filterable: false,
        valueGetter: (params) => params.row.inventoryImages.length,
        renderCell: (params: GridRenderCellParams<MoveRow>) => {
          const imageCount = params.row.inventoryImages.length;
          return <div>{imageCount > 0 ? imageCount : display(null)}</div>;
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        renderCell: (params: GridRenderCellParams<MoveRow>) => (
          <IconButton
            aria-label={`Remove ${params.row.label || params.row.recordId}`}
            onClick={() => removeRow(params.row.id)}
          >
            <DeleteIcon />
          </IconButton>
        ),
      },
    ],
    [removeRow],
  );

  return (
    <DataGridPro
      checkboxSelection
      rows={rows}
      columns={columns}
      selectionModel={selected}
      onSelectionModelChange={setSelected}
    />
  );
};

export default function ScanPage() {
  const {
    addCode,
    removeRow,
    rows,
    selected,
    setSelected,
    submitForm,
    errors,
    destinationProps,
    handleExScanInput,
    moving,
    blocker,
    closeBlocker,
    destinationInputRef,
  } = useMoveItems();
  const codeField = useRef<HTMLInputElement | null>(null);

  return (
    <AutoPagePaperLayout>
      <PageBreadcrumbs links={[{ label: "Warehouse" }]} />
      <PageTitle>Move Item</PageTitle>
      <MovementBlocker block={blocker} onClose={closeBlocker} onExited={() => destinationInputRef.current?.focus()} />
      <Grid container justifyContent="space-between" direction="row" alignItems="center" sx={{ p: "10px", pr: "10px" }}>
        <Formik
          initialValues={{ code: "" }}
          onSubmit={async (values, { resetForm }) => {
            if (values.code) await addCode(values.code).then(() => resetForm());
          }}
        >
          {({ handleSubmit, getFieldProps }) => (
            <form onSubmit={handleSubmit}>
              <TextField
                autoFocus
                autoComplete="off"
                label="Inventory ID/Location ID"
                variant="outlined"
                inputRef={codeField}
                size="small"
                {...getFieldProps("code")}
                onChange={(event) => {
                  handleExScanInput(event, "inventory_id", getFieldProps("code").onChange);
                }}
              />
            </form>
          )}
        </Formik>
      </Grid>
      <Box>
        <Box sx={{ height: "500px", m: "10px" }}>
          <MoveGrid rows={rows} removeRow={removeRow} selected={selected} setSelected={setSelected} />
        </Box>
        <form onSubmit={submitForm}>
          <Box sx={{ display: "flex", justifyContent: "flex-start", m: "10px" }}>
            <TextField
              label="New Location"
              inputRef={destinationInputRef}
              autoComplete="off"
              variant="outlined"
              size="small"
              {...destinationProps}
              onChange={(event) => handleExScanInput(event, "warehouse_location", destinationProps.onChange)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && rows.length === 0) codeField.current?.focus();
              }}
              error={Boolean(errors.destination)}
              helperText={errors.destination}
            />
            <Button
              variant="contained"
              type="submit"
              size="small"
              sx={{ ml: "10px", width: "120px" }}
              endIcon={<ArrowForwardIcon />}
              disabled={
                moving ||
                selected.length === 0 ||
                destinationProps.value === "" ||
                rows.some((row) => selected.includes(row.id) && row.moveFailure)
              }
            >
              Move
            </Button>
          </Box>
        </form>
      </Box>
    </AutoPagePaperLayout>
  );
}
